import { useState, useEffect, useCallback, useRef } from "react";

import {
  ProjectWithChildren,
  ProjectWithChildrenFromJSON,
} from "../bitmaker/schemas/Projects/ProjectWithChildren";

import { useApiError } from "./useApiError";

import { BitmakerAPIClient } from "../services/BitmakerApp";
import { generateFilters } from "../utils";

export const useProjects = () => {
  const [projects, setProjects] = useState<ProjectWithChildren[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [fetchProjectsError, setFetchProjectsError] = useState<string | null>(
    null,
  );
  const [tagsFilterList, setTagsFilterList] = useState<
    Array<{ text: string; value: string }>
  >([]);

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProjects = useCallback(() => {
    if (!isMountedRef.current) return;

    setLoadingProjects(true);
    setFetchProjectsError(null);

    BitmakerAPIClient.get("/projects", null, (response: any) => {
      if (!isMountedRef.current) return;

      const { isError, message } = handleAPIError(response);

      if (isError) {
        console.error("Error fetching projects:", message);
        setFetchProjectsError("Error fetching projects. Please try again.");
        setLoadingProjects(false);
        return;
      }

      const projectRows = Array.isArray(response.data)
        ? (response.data as ProjectWithChildren[]).map(
            ProjectWithChildrenFromJSON,
          )
        : [];

      const tagsFilters = generateFilters(projectRows, (project) =>
        (project.tags || []).map((tag: { name: string }) => tag.name),
      );

      setProjects(projectRows);
      setTagsFilterList(tagsFilters);
      setLoadingProjects(false);
    });
  }, [handleAPIError]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    loadingProjects,
    fetchProjectsError,
    tagsFilterList,
    refetchProjects: fetchProjects,
  };
};
